import styled, { css } from "styled-components";
import {
  BORDER_COLORS,
  COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_CSS } from "../../constants/design/fontSize";
import { BREAKPOINTS } from "../../constants/design/media";
import {
  BORDER_RADIUS,
  SPACING,
} from "../../constants/design/spacing";

export const StripWrapper = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  height: 44px;
  background-color: ${LAYER_COLORS.one};
  border-bottom: 1px solid ${BORDER_COLORS.one};
  padding: 0 ${SPACING.two}px;
  display: flex;
  align-items: center;
  gap: ${SPACING.one}px;
  /* Smooth grow-from-pill when expanding from collapsed mode. */
  animation: stripExpand 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);

  @keyframes stripExpand {
    from {
      max-height: 28px;
      opacity: 0.4;
    }
    to {
      max-height: 44px;
      opacity: 1;
    }
  }

  ${BREAKPOINTS.mobile} {
    height: 40px;
  }
`;

export const Tile = styled.button<{
  active?: boolean;
  dragging?: boolean;
  dragOver?: boolean;
}>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.two}px;
  height: 32px;
  padding: 0 ${SPACING.two}px;
  margin-right: ${SPACING.one}px;
  border-radius: ${BORDER_RADIUS.two}px;
  border: 1px solid transparent;
  background-color: transparent;
  color: ${TEXT_COLORS.one};
  cursor: grab;
  ${FONT_CSS.body.five}
  transition: background-color 0.2s ease, border-color 0.2s ease,
    opacity 0.2s ease, transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1),
    box-shadow 0.2s ease;
  white-space: nowrap;
  user-select: none;
  touch-action: pan-y;

  &:last-child {
    margin-right: 0;
  }

  &:hover {
    background-color: ${LAYER_COLORS.two};
  }

  &:active {
    cursor: grabbing;
  }

  ${({ active }) =>
    active &&
    css`
      background-color: ${LAYER_COLORS.three};
      border-color: ${COLORS.highlight.three};
    `}

  ${({ dragging }) =>
    dragging &&
    css`
      opacity: 0.92;
      transform: scale(1.05);
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.5);
      z-index: 10;
      background-color: ${LAYER_COLORS.three};
    `}

  ${({ dragOver }) =>
    dragOver &&
    css`
      transform: translateX(6px);
      box-shadow: inset 3px 0 0 0 ${COLORS.highlight.one};
    `}
`;

export const TileSymbol = styled.span`
  ${FONT_CSS.body.five}
  color: ${TEXT_COLORS.one};
`;

export const TilePrice = styled.span`
  ${FONT_CSS.data.six}
  color: ${TEXT_COLORS.two};
`;

export const TileChange = styled.span<{ positive: boolean }>`
  ${FONT_CSS.data.six}
  color: ${({ positive }) =>
    positive ? COLORS.up.one : COLORS.down.one};
`;

export const CollapseToggle = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid ${BORDER_COLORS.one};
  background: ${LAYER_COLORS.two};
  border-radius: ${BORDER_RADIUS.one}px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${LAYER_COLORS.three};

    svg {
      color: ${TEXT_COLORS.one};
    }
  }
`;

export const CollapsedBar = styled.div`
  width: 100%;
  height: 28px;
  background-color: ${LAYER_COLORS.one};
  border-bottom: 1px solid ${BORDER_COLORS.one};
  padding: 0 ${SPACING.two}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${FONT_CSS.body.five}
  color: ${TEXT_COLORS.three};
  animation: stripCollapseIn 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);

  @keyframes stripCollapseIn {
    from {
      max-height: 44px;
      opacity: 0.4;
    }
    to {
      max-height: 28px;
      opacity: 1;
    }
  }

  > span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

export const CaretIndicator = styled.span<{ $open?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`;
